import { type EndpointType } from './endpoint_metadata'
import { type PaginationConfig } from './pagination'

interface UrlPreviewState {
  baseUrl: string
  endpoint: EndpointType | null
  parameterValues: Record<string, string>
  pagination: PaginationConfig
}

interface QueryParameter {
  name: string
  value: string
  dynamic: boolean
}

export class UrlPreviewSection {
  constructor (private readonly element: HTMLElement) {}

  update (state: UrlPreviewState): void {
    if (state.endpoint == null || state.endpoint.path === '') {
      this.hide()
      return
    }

    const pathParamNames = this.pathParameterNames(state.endpoint.path)
    const path = this.substitutePathParameters(state.endpoint.path, state.parameterValues)
    const queryParameters = this.queryParameters(state, pathParamNames)

    this.render(this.requestBase(state.baseUrl, path), queryParameters)
    this.element.classList.remove('hidden')
  }

  private hide (): void {
    this.element.replaceChildren()
    this.element.classList.add('hidden')
  }

  private requestBase (baseUrl: string, path: string): string {
    const normalizedBaseUrl = baseUrl.replace(/\/+$/, '')
    const normalizedPath = path.replace(/^\/+/, '')

    return `${normalizedBaseUrl}/${normalizedPath}`
  }

  private pathParameterNames (path: string): Set<string> {
    const names = new Set<string>()
    const matches = path.matchAll(/\{([^}]+)\}/g)

    for (const match of matches) {
      if (match[1] !== '') names.add(match[1])
    }

    return names
  }

  private substitutePathParameters (path: string, values: Record<string, string>): string {
    return path.replace(/\{([^}]+)\}/g, (placeholder, name: string) => {
      const value = this.normalizeParameterValue(values[name] ?? '')
      return value !== '' ? value : placeholder
    })
  }

  private queryParameters (state: UrlPreviewState, pathParamNames: Set<string>): QueryParameter[] {
    const dynamic = this.dynamicPaginationParameter(state.pagination)
    const parameters: QueryParameter[] = []

    ;(state.endpoint?.parameters ?? []).forEach(parameter => {
      if (pathParamNames.has(parameter.name)) return

      if (dynamic?.name === parameter.name) {
        parameters.push(dynamic)
        return
      }

      const value = this.normalizeParameterValue(state.parameterValues[parameter.name] ?? '')
      if (value === '') return

      parameters.push({ name: parameter.name, value, dynamic: false })
    })

    return parameters
  }

  private dynamicPaginationParameter (pagination: PaginationConfig): QueryParameter | null {
    if (pagination.mode !== 'manual' || pagination.request_param === '') return null

    const value = pagination.type === 'from_response'
      ? this.responseValuePlaceholder(pagination.path, pagination.request_param)
      : `{${pagination.request_param}}`

    return { name: pagination.request_param, value, dynamic: true }
  }

  private responseValuePlaceholder (path: string, fallback: string): string {
    const leaf = path.split('.').filter(segment => segment !== '').pop()
    return `{${leaf ?? fallback}}`
  }

  private render (base: string, parameters: QueryParameter[]): void {
    const fragment = document.createDocumentFragment()
    fragment.appendChild(document.createTextNode(base))

    parameters.forEach((parameter, index) => {
      fragment.appendChild(document.createTextNode(index === 0 ? '?' : '&'))
      fragment.appendChild(document.createTextNode(`${this.escapeQueryComponent(parameter.name)}=`))
      fragment.appendChild(this.parameterValueNode(parameter))
    })

    this.element.replaceChildren(fragment)
  }

  private parameterValueNode (parameter: QueryParameter): Node {
    if (!parameter.dynamic) {
      return document.createTextNode(this.escapeQueryComponent(parameter.value))
    }

    const span = document.createElement('span')
    span.classList.add('font-semibold', 'text-accent')
    span.dataset.cy = 'request-url-preview-dynamic-value'
    span.textContent = parameter.value
    return span
  }

  private normalizeParameterValue (value: string): string {
    return value.trim().replace(/ +/g, ' ')
  }

  private escapeQueryComponent (value: string): string {
    return encodeURIComponent(value)
      .replace(/%20/g, '+')
      .replace(/[!'()*]/g, char => `%${char.charCodeAt(0).toString(16).toUpperCase()}`)
  }
}
